'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Search, SlidersHorizontal, X, ArrowUpDown, Heart } from 'lucide-react';
import PackageCard from './PackageCard';
import CompareDrawer from './CompareDrawer';
import PriceAlertModal from './PriceAlertModal';
import { allPackages, type HealthPackage } from './packagesData';
import { useAuth } from '@/contexts/AuthContext';
import {
  getFavouritePackageIds,
  addFavouritePackage,
  removeFavouritePackage,
} from '@/lib/services/patientDataService';

const categories = [
  { id: 'cat-all', label: 'All Packages', count: allPackages.length },
  { id: 'cat-individual', label: 'Individual', count: allPackages.filter((p) => p.category === 'Individual').length },
  { id: 'cat-corporate', label: 'Corporate', count: allPackages.filter((p) => p.category === 'Corporate').length },
  { id: 'cat-executive', label: 'Executive', count: allPackages.filter((p) => p.category === 'Executive').length },
  { id: 'cat-family', label: 'Family', count: allPackages.filter((p) => p.category === 'Family').length },
  { id: 'cat-senior', label: 'Senior Citizen', count: allPackages.filter((p) => p.category === 'Senior Citizen').length },
];

const sortOptions = [
  { id: 'sort-popular', label: 'Most Popular' },
  { id: 'sort-price-asc', label: 'Price: Low to High' },
  { id: 'sort-price-desc', label: 'Price: High to Low' },
  { id: 'sort-tests', label: 'Most Tests' },
  { id: 'sort-discount', label: 'Highest Discount' },
];

export default function PackagesCatalogue() {
  const { user } = useAuth();

  const [activeCategory, setActiveCategory] = useState('cat-all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('sort-popular');
  const [compareList, setCompareList] = useState<HealthPackage[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [fastingOnly, setFastingOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Favourites — synced with Supabase when logged in, localStorage fallback
  const [favourites, setFavourites] = useState<string[]>([]);
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);

  // Price alert modal
  const [alertPkg, setAlertPkg] = useState<HealthPackage | null>(null);

  // Load favourites on mount / user change
  useEffect(() => {
    if (user?.id) {
      getFavouritePackageIds(user.id).then((ids) => {
        setFavourites(ids);
        // Sync to localStorage as cache
        localStorage.setItem('favouritePackages', JSON.stringify(ids));
      });
    } else {
      // Not logged in — use localStorage
      const stored = localStorage.getItem('favouritePackages');
      if (stored) {
        try { setFavourites(JSON.parse(stored)); } catch { setFavourites([]); }
      }
    }
  }, [user?.id]);

  const toggleFavourite = useCallback(async (id: string) => {
    const pkg = allPackages.find((p) => p.id === id);
    const isFav = favourites.includes(id);

    // Optimistic update
    const next = isFav ? favourites.filter((f) => f !== id) : [...favourites, id];
    setFavourites(next);
    localStorage.setItem('favouritePackages', JSON.stringify(next));

    if (user?.id && pkg) {
      try {
        if (isFav) {
          await removeFavouritePackage(user.id, id);
        } else {
          await addFavouritePackage(user.id, id, pkg.name);
        }
      } catch {
        // Revert on error
        setFavourites(favourites);
        localStorage.setItem('favouritePackages', JSON.stringify(favourites));
      }
    }
  }, [favourites, user?.id]);

  const filtered = useMemo(() => {
    let list = [...allPackages];

    if (showFavouritesOnly) {
      list = list.filter((p) => favourites.includes(p.id));
    }

    if (activeCategory !== 'cat-all') {
      const catLabel = categories.find((c) => c.id === activeCategory)?.label;
      list = list.filter((p) => p.category === catLabel);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.includes.some((i) => i.toLowerCase().includes(q))
      );
    }

    list = list.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    if (fastingOnly) list = list.filter((p) => p.fasting);

    switch (sortBy) {
      case 'sort-price-asc':
        list.sort((a, b) => a.price - b.price);
        break;
      case 'sort-price-desc':
        list.sort((a, b) => b.price - a.price);
        break;
      case 'sort-tests':
        list.sort((a, b) => b.tests - a.tests);
        break;
      case 'sort-discount':
        list.sort((a, b) => b.discount - a.discount);
        break;
      default:
        list.sort((a, b) => (b.popular ? 1 : 0) - (a.popular ? 1 : 0));
    }

    return list;
  }, [activeCategory, search, sortBy, priceRange, fastingOnly, showFavouritesOnly, favourites]);

  const toggleCompare = (pkg: HealthPackage) => {
    setCompareList((prev) => {
      const exists = prev.find((p) => p.id === pkg.id);
      if (exists) return prev.filter((p) => p.id !== pkg.id);
      if (prev.length >= 3) return [...prev.slice(1), pkg];
      return [...prev, pkg];
    });
  };

  const isInCompare = (id: string) => compareList.some((p) => p.id === id);

  return (
    <div>
      {/* Sticky filter bar */}
      <div className="bg-card border border-border rounded-2xl p-5 mb-6 sticky top-20 z-30 shadow-card">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search packages or included tests..."
              className="input-field pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <ArrowUpDown size={15} className="text-muted-foreground flex-shrink-0" />
            <select
              className="input-field w-auto text-sm"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {sortOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Favourites toggle */}
          <button
            onClick={() => setShowFavouritesOnly(!showFavouritesOnly)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
              showFavouritesOnly
                ? 'border-red-400 bg-red-50 text-red-600' :'border-border hover:border-red-300 text-muted-foreground hover:text-red-500'
            }`}
          >
            <Heart size={15} className={showFavouritesOnly ? 'fill-red-500 text-red-500' : ''} />
            Favourites
            {favourites.length > 0 && (
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
                showFavouritesOnly ? 'bg-red-100 text-red-600' : 'bg-muted text-muted-foreground'
              }`}>
                {favourites.length}
              </span>
            )}
          </button>

          {/* Advanced filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
              showFilters ? 'border-primary bg-secondary text-primary' : 'border-border hover:border-primary text-muted-foreground'
            }`}
          >
            <SlidersHorizontal size={15} />
            Filters
            {fastingOnly && <span className="w-2 h-2 rounded-full bg-primary" />}
          </button>
        </div>

        {/* Advanced filters panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-5 animate-fade-in">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-2">
                Price Range: ₹{priceRange[0].toLocaleString('en-IN')} – ₹{priceRange[1].toLocaleString('en-IN')}
              </label>
              <input
                type="range"
                min={0}
                max={10000}
                step={100}
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>₹0</span>
                <span>₹10,000</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setFastingOnly(!fastingOnly)}
                className={`w-11 h-6 rounded-full transition-colors duration-200 ${fastingOnly ? 'bg-primary' : 'bg-border'}`}
                aria-label="Toggle fasting filter"
              >
                <span className={`block w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ml-1 ${fastingOnly ? 'translate-x-5' : ''}`} />
              </button>
              <div>
                <div className="text-sm font-medium text-foreground">Fasting Required Packages Only</div>
                <div className="text-xs text-muted-foreground">Show packages that need prior fasting</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Category tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => { setActiveCategory(cat.id); setShowFavouritesOnly(false); }}
            className={`filter-tab flex items-center gap-1.5 flex-shrink-0 ${activeCategory === cat.id && !showFavouritesOnly ? 'active' : ''}`}
          >
            {cat.label}
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              activeCategory === cat.id && !showFavouritesOnly ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'
            }`}>
              {cat.count}
            </span>
          </button>
        ))}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          {showFavouritesOnly
            ? `${filtered.length} saved package${filtered.length !== 1 ? 's' : ''}`
            : `${filtered.length} package${filtered.length !== 1 ? 's' : ''} found`}
        </p>
        {compareList.length > 0 && (
          <button
            onClick={() => setShowCompare(true)}
            className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-blue-700 transition-colors"
          >
            Compare ({compareList.length})
          </button>
        )}
      </div>

      {/* Package grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Heart size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
          <p className="text-muted-foreground font-medium">
            {showFavouritesOnly
              ? user
                ? 'No saved packages yet. Click the ❤️ on any package to save it.' :'Sign in to save packages across devices, or click ❤️ to save locally.' :'No packages match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              isInCompare={isInCompare(pkg.id)}
              onToggleCompare={() => toggleCompare(pkg)}
              isFavourite={favourites.includes(pkg.id)}
              onToggleFavourite={() => toggleFavourite(pkg.id)}
              onPriceAlert={() => setAlertPkg(pkg)}
            />
          ))}
        </div>
      )}

      {/* Compare drawer */}
      {compareList.length > 0 && (
        <CompareDrawer
          packages={compareList}
          onRemove={(id) => setCompareList((prev) => prev.filter((p) => p.id !== id))}
          onClear={() => setCompareList([])}
          showModal={showCompare}
          onShowModal={() => setShowCompare(true)}
          onCloseModal={() => setShowCompare(false)}
        />
      )}

      {/* Price alert modal */}
      {alertPkg && (
        <PriceAlertModal
          packageId={alertPkg.id}
          packageName={alertPkg.name}
          currentPrice={alertPkg.price}
          onClose={() => setAlertPkg(null)}
        />
      )}
    </div>
  );
}